import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { Actions } from "../../Action";
import _debounce from "lodash/debounce";
import { sendQuestionDraftData } from "../../API/api";
import Styles from "./NewEditorStyles.module.css";
import TopBar from "../../Component/Editor/TopBar";
import NewTopBar from "./NewTopBar";
import NewBottomBar from "./NewBottomBar";
import NewEditorSubmitButtons from "./NewEditorSubmitButtons";
import { useNavigate } from "react-router-dom";
import NewEditorExtraTabs from "./NewEditorExtraTabs";
import { getAttachments } from "../../Action/actions";
import { defaultPdfRules, transformForPdf } from "./NewEditorTextTranformer";

// ---Read only Table Embed ---
const BlockEmbed = Quill.import("blots/block/embed");

class HtmlTableBlot extends BlockEmbed {
  static blotName = "html-table";
  static tagName = "DIV";
  static className = "ql-html-table";

  static create(tableHtml) {
    const node = super.create();
    node.setAttribute("contenteditable", "false");
    node.innerHTML = HtmlTableBlot.sanitize(tableHtml);
    return node;
  }

  static value(node) {
    return node.innerHTML;
  }

  static sanitize(html) {
    const allowedTags = new Set([
      "TABLE",
      "THEAD",
      "TBODY",
      "TFOOT",
      "TR",
      "TH",
      "TD",
      "COLGROUP",
      "COL",
    ]);
    const keepAttrs = new Set([
      "rowspan",
      "colspan",
      "style",
      "width",
      "height",
      "align",
      "valign",
      "border",
      "cellpadding",
      "cellspacing",
    ]);

    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    const walker = document.createTreeWalker(tmp, NodeFilter.SHOW_ELEMENT);
    const toStrip = [];
    while (walker.nextNode()) {
      const el = walker.currentNode;
      if (!allowedTags.has(el.tagName)) {
        toStrip.push(el);
      } else {
        [...el.attributes].forEach((attr) => {
          if (!keepAttrs.has(attr.name.toLowerCase())) {
            el.removeAttribute(attr.name);
          }
        });
      }
    }
    toStrip.forEach((el) =>
      el.replaceWith(document.createTextNode(el.textContent || ""))
    );
    return tmp.innerHTML;
  }
}

Quill.register(HtmlTableBlot);

function extractBodyHTML(raw = "") {
  try {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    const body = doc?.body?.innerHTML?.trim() || raw.trim();
    return body;
  } catch {
    return raw.trim();
  }
}

function NewEditorText() {
  const dispatch = useDispatch();
  const {
    updateAnswerTextForEditor,
    updateButtonEnableForEditor,
    advanceFilterModalToggle,
    getAttachments,
    getScrollPositionInExpertOpen,
  } = bindActionCreators(Actions, dispatch);

  const editorState = useSelector((state) => state.editor);
  const navigate = useNavigate();

  // Store the original text separately
  const [originalText, setOriginalText] = useState("");
  const [isEdited, setIsEdited] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const debounceFn = useCallback(_debounce(handleDebounceFn, 7000), []);

  const stripHtml = (str) => str.replace(/<[^>]*>?/gm, "").trim();

  function handleDebounceFn(inputValue) {
    if (inputValue !== "") {
      const bodyOnly = extractBodyHTML(inputValue);
      const patched = transformForPdf(bodyOnly, defaultPdfRules);

      const payload = {
        questionUuid: editorState.data.questionUuid,
        answer: patched,
        uploadFiles: [],
      };
      sendQuestionDraftData(payload).then((res) => {});
    }
  }

  const debounceRef = useRef(_debounce((val) => handleDebounceFn(val), 7000));
  useEffect(() => () => debounceRef.current.cancel(), []);

  const quillRef = useRef(null);

  useEffect(() => {
    const incoming = editorState.answerText || "";
    const clean = extractBodyHTML(incoming);
    if (clean !== incoming) updateAnswerTextForEditor(clean);
  }, [editorState.data?.questionUuid]);

  // after first render, when the ReactQuill editor is ready:
  useEffect(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const clipboard = editor.getModule("clipboard");

    // Map H1/H2/H3 to header only; drop bold/size on those blocks
    ["H1", "H2", "H3"].forEach((tag, idx) => {
      clipboard.addMatcher(tag, (node, delta) => {
        delta.ops.forEach((op) => {
          op.attributes = { ...(op.attributes || {}), header: idx + 1 };
          if (op.attributes.bold) delete op.attributes.bold;
          if (op.attributes.size) delete op.attributes.size;
        });
        return delta;
      });
    });

    // For other elements, map inline styles — but skip headings
    clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      const el = node;
      const isHeading = ["H1", "H2", "H3"].includes(el?.tagName);
      if (el?.style) {
        const ta = el.style.textAlign;
        if (ta) {
          delta.ops.forEach((op) => {
            op.attributes = { ...(op.attributes || {}), align: ta };
          });
        }
      }
      return delta;
    });
  }, []);

  // Update original text when new data is loaded or component initializes
  useEffect(() => {
    const currentText = extractBodyHTML(editorState.answerText || "");
    // console.log('useEffect triggered - setting original text to:', currentText);
    // console.log('questionUuid:', editorState.data?.questionUuid);

    setOriginalText(currentText);
    setIsInitialized(true);
  }, [editorState.data?.questionUuid]); // Trigger when questionUuid changes (new data loaded)

  // Reset isEdited to false after everything is loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsEdited(false);
      // console.log('Force set isEdited to false after 1 second');
    }, 1000);

    return () => clearTimeout(timer); // Cleanup timer
  }, []);

  const normalizeDeltaForPublish = (delta) => {
    if (!delta?.ops) return delta;
    const ops = delta.ops.map((op) => {
      if (!op.attributes) return op;
      const a = { ...op.attributes };
      if (a.header) {
        delete a.bold;
        delete a.size;
      }
      return { ...op, attributes: a };
    });
    return { ops };
  };

  const buildPublishPayload = () => {
    const editor = quillRef.current?.getEditor();
    const rawDelta = editor?.getContents();
    const delta = normalizeDeltaForPublish(rawDelta);

    // OPTIONAL: produce semantic HTML client-side
    // const htmlForPdf = new QuillDeltaToHtmlConverter(delta.ops, {/* options */}).convert();

    return {
      questionUuid: editorState.data.questionUuid,
      answerDelta: delta,
      // answerHtmlForPdf: htmlForPdf,  // if your backend wants HTML now
    };
  };

  const handlePublish = async () => {
    const payload = buildPublishPayload();
    await sendQuestionDraftData(payload);
    setIsEdited(false);
  };

  const getEditorPayload = () => {
    const editor = quillRef.current?.getEditor();
    const rawDelta = editor?.getContents();
    const delta = normalizeDeltaForPublish(rawDelta);
    const html = editor?.root?.innerHTML || "";

    return {
      answerHtml: html, // keep your image wrapper behavior
      answerDelta: delta,
      uploadFiles: editorState.attachments ?? [],
    };
  };
  const updateText = (event) => {
    // console.log('updateText called with:', event);
    // console.log('Is initialized:', isInitialized);

    const pureStr = event.replace(/<[^>]*>?/gm, "");
    updateButtonEnableForEditor(pureStr === "" ? true : false);
    updateAnswerTextForEditor(event);

    // Compare current text with original text directly
    const currentText = stripHtml(event);
    const originalTextStripped = stripHtml(originalText);

    // console.log('Current (stripped):', `"${currentText}"`);
    // console.log('Original (stripped):', `"${originalTextStripped}"`);

    const hasChanged = currentText !== originalTextStripped;
    // console.log("Has changed:", hasChanged);

    setIsEdited(hasChanged);

    if (pureStr !== "") {
      debounceFn(event);
    }
  };

  // Optional: Function to reset the original text (call this after saving)
  const resetOriginalText = () => {
    setOriginalText(editorState.answerText || "");
    setIsEdited(false);
  };

  const modules = useMemo(
    () => ({
      history: { delay: 1000, maxStack: 100, userOnly: true },
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          [{ color: [] }, { background: [] }],
          ["link", "image"],
          ["insertHtmlTable"],
        ],
        handlers: {
          insertHtmlTable: function () {
            const q = this.quill;
            const template =
              '<table border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse;width:100%">' +
              "<tbody>" +
              "<tr><td>Cell 1</td><td>Cell 2</td></tr>" +
              "<tr><td>Cell 3</td><td>Cell 4</td></tr>" +
              "</tbody>" +
              "</table>";
            const range = q.getSelection(true);
            const index = range ? range.index : q.getLength();
            q.insertEmbed(
              index,
              "html-table",
              HtmlTableBlot.sanitize(template),
              "user"
            );
            q.setSelection(index + 1, 0, "user");
          },
        },
      },
      clipboard: {
        matchVisual: false,
        // Convert any pasted <table> into a single HtmlTableBlot
        matchers: [
          [
            "TABLE",
            (node /* HTMLElement */) => {
              return {
                ops: [
                  {
                    insert: {
                      "html-table": HtmlTableBlot.sanitize(node.outerHTML),
                    },
                  },
                ],
              };
            },
          ],
        ],
      },
    }),
    []
  );

  const formats = [
    "header",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "align",
    "color",
    "background",
    "link",
    "image",
    "html-table",
  ];

  const getBase64 = (file, cb) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      cb(reader.result);
    };
    reader.onerror = function (error) {
      // handle error
    };
  };

  useEffect(() => {
    getScrollPositionInExpertOpen(0);
    getAttachments([]);
    return () => {
      setIsEdited(false);
    };
  }, []);

  const attachmentsData = (file) => {
    advanceFilterModalToggle({ bool: true, value: "addAttachmentCaption" });
    getBase64(file, (result) => {
      const size =
        file.size / (1024 * 1024) > 1
          ? file.size / (1024 * 1024) + " mB"
          : file.size / 1024 + " kB";

      const data = {
        caption: "",
        name: file.name,
        size: size,
        type: file.type,
        src: result,
        show: 1,
        isThumbNail: false,
      };

      editorState.compareAttachments.push(data);
      editorState.attachments.push(data);
    });
  };

  return (
    <div style={{ padding: "1px", height: "100%" }}>
      <NewTopBar />

      <input
        type="file"
        id="actual-btn"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            attachmentsData(file);
          }
        }}
        style={{ display: "none" }}
        name="files"
        required=""
        maxsize="500"
        aria-invalid="true"
      />
      <label htmlFor="actual-btn">
        <i
          className="fa fa-paperclip"
          style={{
            position: "absolute",
            marginLeft: "525px",
            marginTop: "20px",
            color: "#434343",
            fontSize: "16px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        ></i>
      </label>
      <div className={Styles.editor}>
        <ReactQuill
          theme="snow"
          ref={quillRef}
          value={editorState.answerText}
          modules={modules}
          formats={formats}
          onChange={updateText}
          style={{
            height: "500px",
            // overflowY:"auto"
          }}
        />
      </div>
      <br />
      <br />
      <NewBottomBar />
      <NewEditorSubmitButtons
        isEdited={isEdited}
        getEditorPayload={getEditorPayload}
        transformForPdf={transformForPdf}
      />
      <br />
      <br />
      <NewEditorExtraTabs />
    </div>
  );
}

export default NewEditorText;
